import React from 'react';
import LinearGradient, { LinearGradientProps } from '@/components/shared/gradient/LinearGradient';
import classNames from 'classnames';
import { AbsoluteLink } from '@/components/AbsoluteLink';
import { gameThumbImgMexico } from '@/utils/image-utils';
import { GameCardVM } from '@/models/vm/game-card-vm.model';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { useLogin } from '@/hooks/useLogin';
import { useIntl } from 'react-intl';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { PlayIcon } from '@heroicons/react/24/solid';
import { useLocation } from 'react-router-dom';
import { envIsMexico } from '@/constants/constants';
import GameTag from '@/pages/games/game-card/GameTag';
import JackpotCurrency from '@/components/shared/jackpot-currency/JackpotCurrency';
import { selectIsGameFavourited } from '@/store/games/selectors';
import BffApiService from '@/shared/bffApiService';
import { getFavouriteGames } from '@/store/games/actions';
import FavouriteGameTag from '@/pages/games/game-card/FavouriteGameTag';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';

const OverlayButton = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-blue-blue px-3 py-2 rounded-3xl text-sm text-white font-bold">{children}</div>
);

const mexicoGradientProps = {
  from: 'rgba(255, 162, 10, 0.6)',
  to: 'rgba(254, 118, 200)',
  direction: 'bottom right',
  transitionPoint: '46%',
} as LinearGradientProps;

const normalGradientProps = {
  from: 'rgba(42, 140, 155, 0.6)',
  to: 'rgba(10, 62, 107, 0.8)',
  direction: 'bottom right',
  transitionPoint: '46%',
} as LinearGradientProps;

const GameCard = ({
  game,
  large = false,
  category,
  ...props
}: {
  game: GameCardVM;
  large?: boolean;
  category: string;
}) => {
  const gradientProps = envIsMexico() ? mexicoGradientProps : normalGradientProps;
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();
  const gameId = game.gameId;
  const isGameFavourited = useSelector(selectIsGameFavourited(gameId));
  const { request: favouriteGameRequest, loading: favouriteGameLoading } = useBffApiRequest<null>();
  const { request: removeFromFavouriteGamesRequest, loading: removeFromFavouriteGamesLoading } =
    useBffApiRequest<null>();

  const { openLogin } = useLogin();
  const intl = useIntl();
  const location = useLocation();

  const shouldOpenLogin = !isAuthenticated && !game.isDemoAvailable;

  const onCardClick = (e?: React.MouseEvent<HTMLAnchorElement>): void => {
    if (shouldOpenLogin) {
      e?.preventDefault();
      openLogin({ openedFrom: OpenedFrom.Games });
    } else {
      triggerGTag(GTagEvents.open_game, {
        openedFrom: location.pathname,
        categoryName: category,
        gameName: game.name,
      });
    }
  };

  const onFavouriteClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (isGameFavourited) {
      removeFromFavouriteGamesRequest({
        apiMethod: BffApiService.removeFromFavouriteGames,
        pathParams: { gameId },
        successCallback: () => dispatch(getFavouriteGames()),
      });
      return;
    }
    if (!isGameFavourited) {
      favouriteGameRequest({
        apiMethod: BffApiService.favouriteGame,
        pathParams: { gameId },
        successCallback: () => dispatch(getFavouriteGames()),
      });
      return;
    }
  };

  const overlayIcon = shouldOpenLogin ? (
    <OverlayButton>{intl.formatMessage({ id: 'gameCard.loginToPlay' })}</OverlayButton>
  ) : (
    <PlayIcon width={40} height={40} fill="white" />
  );

  return (
    <AbsoluteLink
      to={`/games/${game.slug}/${game.gameId}`}
      title={game.name}
      onClick={onCardClick}
      className={classNames(
        'relative rounded-xl w-full h-full overflow-hidden flex flex-col snap-start shadow-sm',
      )}
      data-cy={`game-card-mc-${game.slug}`}
      {...props}
    >
      <div className="absolute top-0 right-0 z-[2]">
        {game.isLive && <GameTag type="live" />}
        {game.isNew && <GameTag type="new" />}
        {game.isHot && <GameTag type="hot" />}
      </div>
      <LinearGradient {...gradientProps} />
      <div className="relative z-[1] flex flex-col justify-end h-full">
        <img
          className="w-full h-full object-cover object-top"
          alt={game.name}
          src={gameThumbImgMexico(game.slug, large)}
        />
        {game.jackpot && (
          <div className="absolute inset-x-0 bottom-12 px-3">
            <div className="w-full text-center p-1 rounded-lg bg-black/60 text-white font-bold">
              <JackpotCurrency
                currencyCode={game.jackpot.currencyCode}
                amount={game.jackpot.amount}
              />
            </div>
          </div>
        )}
        <div className="bg-black/60 py-2.5 px-2 text-[14px] leading-[15px] xs:leading-[18px] truncate text-center text-white">
          {game.shortDesc}
        </div>
        {overlayIcon && (
          <div className="group bg-transparent absolute inset-0 flex rounded-xl hover:bg-black/50 justify-center items-center transition-all duration-200 ease-in-out">
            <div className="hidden group-hover:block">{overlayIcon}</div>
            {isAuthenticated && (
              <div
                onClick={onFavouriteClick}
                className="block md:group-hover:block md:hidden absolute top-1 left-1 md:top-2 md:left-2 z-[3] bg-black/60 p-1.5 rounded-2xl md:bg-transparent md:p-0 md:rounded-none"
              >
                <FavouriteGameTag
                  isFavourited={isGameFavourited}
                  loading={favouriteGameLoading || removeFromFavouriteGamesLoading}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </AbsoluteLink>
  );
};

export default GameCard;
